/* eslint-disable react/prop-types */
import { isSodimacStandalone } from '@digital-retail/store-manager';
import { cx } from 'cvb';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import constants from '../../config/constants';
import { withApplicationContext } from '../../utils/ApplicationContext';
import _ from '../../utils/LodashImports';
import { ALLOWED_CONFIGS_FOR_UNIT_PRICES } from '../../utils/PriceHelpers';
import { withProduct } from '../../utils/ProductContext';
import { withUser } from '../../utils/UserContext';
import { getVariant } from '../../utils/variant';
import CesPrices from '../CesPrices/CesPrices';
import Fomo from '../Fomo/Fomo';
import MyLists from '../MyLists/MyLists';
import ProPrice from '../ProPrice/ProPrice';
import BodyCopy from '../ui/BodyCopy/BodyCopy';
import Price from './Price/Price';
import CrossPrice from '../CrossPrices/CrossPrices';
import { pricesStyles } from './Prices.style';
import priceStylesConfig, {
  newPriceStylesConfig,
  newUnitPriceStyleConfig,
  oldPriceStyles,
  priceSizes,
  unitPriceStylesConfig,
} from './PriceStyleConfig';
import Skeleton from './Skeleton/Skeleton';

const WholesaleBadge = dynamic(() => import('../WholesaleBadge/WholesaleBadge'), {
  ssr: false,
});

const Prices = ({
  priceList,
  productId,
  variantId,
  offeringId,
  isTabApplicable,
  page,
  layout,
  regionCode,
  variant,
  isSponsoredProduct,
  isConnectProduct,
  operators,
  selectedOperatorId,
  appCtx,
  discountBadge,
  priceCount,
  isOutOfStock,
  labels,
  product,
  showMyList,
  measurement,
  darSectionActive,
  showFomo,
  userSegments,
  isUserLoggedIn,
  showCrossPrice,
}) => {
  const { store, deviceType } = appCtx;
  const isSoCom = isSodimacStandalone(store);
  const isPDP = page === constants.PAGES.PDP;
  const { PRICE_TYPE } = constants;

  const highlitedPriceTypes = [PRICE_TYPE.internet, PRICE_TYPE.event];
  const currentVariant =
    (product && product.variants && product.variants.length && getVariant(product.variants, variantId)) || undefined;
  const comboPricesConfig = {
    perComboText: _.get(appCtx, 'siteConfig.textDictionary.PER_COMBO_TEXT', '/ Combo'),
    comboTotalPriceText: _.get(appCtx, 'siteConfig.textDictionary.COMBO_TOTAL_PRICE_TEXT', 'Total por separado'),
    comboPriceIndex: 2,
  };

  const isWholesaleEnabled = _.get(appCtx, 'siteConfig.toggles.isWholesaleEnabled', false);

  const timeOutSkeleton = _.get(appCtx, 'siteConfig.configurations.TIME_OUT_SKELETON', 1000);
  const showSkeletonFlag = _.get(appCtx, 'siteConfig.toggles.SHOW_SKELETON', false);
  const cesSegment = _.get(appCtx, 'siteConfig.configurations.CES_SEGMENT', {});
  const userCESSegment = cesSegment[userSegments?.find((segment) => cesSegment[segment.name])?.name];
  const [showSkeleton, setShowSkeleton] = useState(isSoCom);

  const isMyListsEnabled = _.get(appCtx, 'siteConfig.toggles.isPDPMyListsEnabled', false) && showMyList;
  const isCrossPriceEnabled = _.get(appCtx, 'siteConfig.toggles.isCrossPriceEnabled', false);

  const pricesCount = (priceList || []).length;
  const isCmrPrice = currentVariant?.prices?.some((price) => price.type === PRICE_TYPE.cmr);

  const cesPrice = currentVariant?.promotions?.find((promotion) => promotion.campaignName === userCESSegment) || null;
  const proPrice =
    currentVariant?.promotions?.find(
      (promotion) =>
        promotion.campaignName === constants.PRICE_TYPE.pro || promotion.campaignName === constants.PRICE_TYPE.bulkPrice
    ) || null;

  const filteredPrices = currentVariant?.prices?.filter((price) => price.type !== constants.PRICE_TYPE.cmr) || [];

  const lowestPrice = filteredPrices?.length
    ? filteredPrices.reduce((min, price) => (parseFloat(price.price[0]) > parseFloat(min.price[0]) ? min : price))
    : null;

  const cesPriceIsLower = parseFloat(cesPrice?.prices[0]?.price[0]) < parseFloat(lowestPrice?.price[0]);
  const proPriceIsLower = parseFloat(proPrice?.prices[0]?.price[0]) < parseFloat(lowestPrice?.price[0]);

  const isNormalPriceCrossedOut = _.get(appCtx, 'siteConfig.toggles.isNormalPriceCrossedOut', false);

  const isNewPriceApplicable = _.get(appCtx, 'siteConfig.toggles.isNewPriceApplicable', false);
  const showUnitPrice = !!ALLOWED_CONFIGS_FOR_UNIT_PRICES.includes(variant);
  const isModernLineContainerEnabled = !!appCtx?.siteConfig?.toggles?.isModernLineContainerEnabled;

  const shouldShowFomo = showFomo && product?.variants?.length <= 1 && product?.variants?.[0]?.fomo?.enabled === true;
  const hasOffers = priceList && priceList.some ? priceList.some((p) => p.crossed) : false;
  const getPriceImportance = (icon, crossed, isSep) => {
    if (icon && isSep) return '';

    if (icon === 'cmr-icon') {
      return 'high';
    }

    if (crossed) {
      return isNewPriceApplicable ? 'medium' : 'low';
    }

    if (isNewPriceApplicable) {
      if (isSponsoredProduct) {
        return 'septenary';
      }
      return 'senary';
    }
    return '';
  };

  const getPriceConfig = (
    config,
    icon,
    crossed = false,
    isSep = false,
    hasUnitStyles = false,
    pricePosition = 0,
    isComboPrice = false
  ) => {
    const priceConfig = hasUnitStyles ? newUnitPriceStyleConfig : newPriceStylesConfig;

    const variantPriceConfig = priceConfig[variant];
    const lineHeight = _.get(variantPriceConfig, `lineHeight[${pricePosition}]`, '');
    const lineHeightCrossed = _.get(variantPriceConfig, `lineHeight[${2}]`, '');
    const importance = getPriceImportance(icon, crossed, isSep);
    if (isNormalPriceCrossedOut) {
      // **
      let priceSizeNotCrossed = config.size;

      if (!isComboPrice && priceList.length <= 2 && highlitedPriceTypes.includes(priceList[pricePosition].type)) {
        priceSizeNotCrossed = variant === 'CART' ? 'copy10' : config.size;
      }
      if (darSectionActive && variant !== 'CART') {
        priceSizeNotCrossed = 'copy15';
      }
      // **
      return {
        ...config,
        importance,
        size: isNewPriceApplicable && crossed ? variantPriceConfig.crossed : priceSizeNotCrossed,
        lineHeight: isNewPriceApplicable && !crossed ? lineHeight : lineHeightCrossed,
      };
    }
    return isNewPriceApplicable
      ? {
          ...config,
          importance,
          lineHeight,
        }
      : config;
  };

  const getUnitPrice = (unitPrice, symbol) => {
    if (!unitPrice || !showUnitPrice) {
      return '';
    }
    const { price, unit } = unitPrice;
    return price?.map((p) => `${symbol} ${p} ${unit}`).join(' - ');
  };

  const getPrice = (price, symbol) => {
    if (!price || !Array.isArray(price)) {
      return '';
    }
    return price?.map((p) => `${symbol} ${p}`).join(' - ');
  };

  const priceType = {
    PDP: 'pdp-prices',
    POD: 'pod-prices',
    PLP: 'pod-prices',
    CART: 'cart-prices',
    SLP: 'pod-prices',
  };

  const getPriceStyle = (hasUnitStyles = false) => {
    if (isNewPriceApplicable) {
      if (hasUnitStyles) {
        return newUnitPriceStyleConfig[variant][priceList.length && priceList[0].icons ? 'cmr' : 'noCmr'];
      }
      return newPriceStylesConfig[variant][pricesCount && priceList[0].icons ? 'cmr' : 'noCmr'];
    }

    if (isNormalPriceCrossedOut) {
      if (hasUnitStyles) {
        return unitPriceStylesConfig[variant][priceList.length && priceList[0].icons ? 'cmr' : 'noCmr'];
      }
      return priceStylesConfig[variant][pricesCount && priceList[0].icons ? 'cmr' : 'noCmr'];
    }

    return oldPriceStyles[variant];
  };

  const priceConfig = (hasUnitStyles) => getPriceStyle(hasUnitStyles);

  useEffect(() => {
    if (isSoCom || store === 'sodimac') {
      const timer = setTimeout(() => {
        setShowSkeleton(false);
      }, timeOutSkeleton);

      return () => {
        clearTimeout(timer);
      };
    }
    return () => {};
  }, [isSoCom, timeOutSkeleton, store]);

  if (showSkeleton && showSkeletonFlag && !isOutOfStock) return <Skeleton />;

  const renderPrices = () => {
    if (cesPrice && isUserLoggedIn && (isSoCom || store === 'sodimac') && cesPriceIsLower && !isOutOfStock) {
      return (
        <CesPrices
          getPrice={getPrice}
          variant={currentVariant}
          regionCode={regionCode}
          getUnitPrice={getUnitPrice}
          discountBadge={discountBadge}
          isCmrPrice={isCmrPrice}
          proPrice={proPrice}
          cesPrice={cesPrice}
          mode={variant}
        />
      );
    }
    return (
      <>
        {priceList && (
          <ol className={cx(`ol-${layout} ${priceType[page]} fa--prices`, isNewPriceApplicable && 'li-separation')}>
            {priceList.map((priceData, i) => (
              <Price
                key={priceData.price?.[0]}
                priceList={priceList}
                productId={productId}
                variantId={variantId}
                page={page}
                regionCode={regionCode}
                variant={variant}
                isSponsoredProduct={isSponsoredProduct}
                isConnectProduct={isConnectProduct}
                operators={operators}
                selectedOperatorId={selectedOperatorId}
                appCtx={appCtx}
                discountBadge={discountBadge}
                priceCount={priceCount}
                labels={labels}
                product={product}
                measurement={measurement}
                darSectionActive={darSectionActive}
                showFomo={showFomo}
                {...priceData}
                i={i}
              />
            ))}
            {currentVariant?.comboData &&
              currentVariant?.comboData?.showTotalPrice &&
              (() => {
                const isComboPrice = true;
                return (
                  <>
                    <BodyCopy
                      {...getPriceConfig(
                        priceSizes[priceConfig(false)][comboPricesConfig.comboPriceIndex],
                        '',
                        true,
                        true,
                        false,
                        comboPricesConfig.comboPriceIndex,
                        isComboPrice
                      )}
                      crossed
                    >
                      {currentVariant.comboData.totalPrice}
                    </BodyCopy>
                    <BodyCopy
                      {...getPriceConfig(
                        priceSizes[priceConfig(false)][comboPricesConfig.comboPriceIndex],
                        '',
                        true,
                        true,
                        false,
                        comboPricesConfig.comboPriceIndex,
                        isComboPrice
                      )}
                    >
                      &nbsp;/&nbsp;{comboPricesConfig.comboTotalPriceText}
                    </BodyCopy>
                  </>
                );
              })()}
          </ol>
        )}

        {proPrice && !isCmrPrice && (isSoCom || store === 'sodimac') && !isOutOfStock && !isWholesaleEnabled && (
          <ProPrice proPrice={proPrice} regionCode={regionCode} getPrice={getPrice} getUnitPrice={getUnitPrice} />
        )}
        {isWholesaleEnabled && proPrice && proPriceIsLower && !isCmrPrice && !isOutOfStock && (
          <div className="wholesale-container">
            <WholesaleBadge
              quantityToBuyValue={proPrice?.metadata?.quantityToBuyValue}
              quantityToBuyOperator={proPrice.campaignName === constants.PRICE_TYPE.pro ? '>=' : '='}
              discountPercent={Number(proPrice?.metadata?.discountPercent)}
            />
            <ol className={cx(`ol-${layout} ${priceType[page]} fa--prices`, isNewPriceApplicable && 'li-separation')}>
              <Price
                priceList={proPrice.prices}
                productId={productId}
                variantId={variantId}
                offeringId={offeringId}
                isTabApplicable={isTabApplicable}
                page={page}
                layout={layout}
                regionCode={regionCode}
                variant={variant}
                isSponsoredProduct={isSponsoredProduct}
                isConnectProduct={isConnectProduct}
                operators={operators}
                selectedOperatorId={selectedOperatorId}
                appCtx={appCtx}
                priceCount={priceCount}
                isOutOfStock={isOutOfStock}
                labels={labels}
                product={product}
                showMyList={showMyList}
                measurement={measurement}
                darSectionActive={darSectionActive}
                showFomo={showFomo}
                userSegments={userSegments}
                isUserLoggedIn={isUserLoggedIn}
                {...proPrice.prices[0]}
                type="internetPrice"
                i={0}
              />
            </ol>
          </div>
        )}
      </>
    );
  };

  const renderCrossPrice = () => {
    return (
      <CrossPrice
        priceList={priceList}
        regionCode={regionCode}
        discountBadge={discountBadge}
        promotions={currentVariant?.promotions}
        userSegments={userSegments}
        isUserLoggedIn={isUserLoggedIn}
        cesSegment={cesSegment}
      />
    );
  };

  return (
    <div
      className={cx(
        'prices',
        isTabApplicable && 'price-with-tab',
        `prices-${layout}`,
        isNewPriceApplicable && page === constants.PAGES.PDP && variant === constants.VIEW_TEMPLATE.TYPE_C && 'mb-18px',
        isOutOfStock && 'isOutOfStock'
      )}
      id={`testId-pod-prices-${productId}`}
    >
      {!isModernLineContainerEnabled && isPDP && isMyListsEnabled && deviceType === 'desktop' && (
        <div
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <MyLists
            appCtx={appCtx}
            product={{
              productId,
              variantId,
              offeringId,
            }}
            isPDPpage
          />
        </div>
      )}
      {isCrossPriceEnabled && showCrossPrice ? renderCrossPrice() : renderPrices()}
      {shouldShowFomo && !hasOffers && (
        <div className="fomo-box">
          <Fomo product={product} />
        </div>
      )}
      <style jsx>{pricesStyles}</style>
    </div>
  );
};

Prices.defaultProps = {
  productId: '',
  variantId: '',
  offeringId: '',
  isTabApplicable: false,
  page: 'POD',
  layout: '4_GRID',
  regionCode: 'cl',
  variant: 'PDP_MAIN',
  labels: {},
  operators: [],
  selectedOperatorId: undefined,
  isConnectProduct: false,
  isSponsoredProduct: false,
  appCtx: {},
  discountBadge: undefined,
  priceCount: undefined,
  isOutOfStock: false,
  product: {},
  showMyList: true,
  measurement: undefined,
  darSectionActive: false,
  userSegments: [],
  isUserLoggedIn: false,
  showFomo: false,
  showCrossPrice: false,
};

Prices.propTypes = {
  priceList: PropTypes.arrayOf(PropTypes.object).isRequired,
  productId: PropTypes.string,
  variantId: PropTypes.string,
  offeringId: PropTypes.string,
  isTabApplicable: PropTypes.bool,
  page: PropTypes.oneOf(['POD', 'PDP', 'CART', 'SLP', 'PLANS']),
  layout: PropTypes.string,
  regionCode: PropTypes.string,
  variant: PropTypes.string,
  labels: PropTypes.object,
  isSponsoredProduct: PropTypes.bool,
  isConnectProduct: PropTypes.bool,
  operators: PropTypes.arrayOf(PropTypes.object),
  selectedOperatorId: PropTypes.string,
  appCtx: PropTypes.object,
  discountBadge: PropTypes.object,
  priceCount: PropTypes.number,
  isOutOfStock: PropTypes.bool,
  showMyList: PropTypes.bool,
  product: PropTypes.object,
  measurement: PropTypes.object,
  darSectionActive: PropTypes.bool,
  userSegments: PropTypes.array,
  isUserLoggedIn: PropTypes.bool,
  showFomo: PropTypes.bool,
  showCrossPrice: PropTypes.bool,
};

export default withApplicationContext(withProduct(withUser(Prices)));
export { Prices };
